import { useState, useMemo } from 'react';
import TicketsHeader from 'components/tickets/TicketsHeader';
import TicketsFilters from 'components/tickets/TicketsFilters';
import TicketsTable from 'components/tickets/TicketsTable';
import { ticketsData, Ticket } from '@/lib/constants';
import TicketModal from 'components/tickets/TicketModal';

export default function Tickets() {
  const [selectedTime, setSelectedTime] = useState('all');
  const [tickets, setTickets] = useState<Ticket[]>(ticketsData);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [filters, setFilters] = useState({
    status: '',
    customer: '',
    assignedTo: '',
    startDate: null,
    endDate: null,
    statuses: [] as string[],
    search: '',
    organization: ''
  });

  const handleAssigneeChange = (ticketId: string, newAssignee: string) => {
    setTickets(prevTickets => 
        prevTickets.map(ticket => 
            ticket.id === ticketId 
                ? { ...ticket, assignedTo: newAssignee, status: newAssignee ? newAssignee === "unassigned"? "Unassigned": 'Assigned' : 'Open' }
                : ticket
        )
    );
  };

  // Filter tickets based on all filters
  const filteredTickets = useMemo(() => {
    let filtered = [...tickets];
    
    // Status filters
    if (filters.statuses && filters.statuses.length > 0) {
      filtered = filtered.filter(ticket => 
        filters.statuses.includes(ticket.status.toLowerCase())
      );
    }

    // Search filter
    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      filtered = filtered.filter(ticket =>
        ticket.id.toLowerCase().includes(searchTerm) ||
        ticket.customer.toLowerCase().includes(searchTerm) ||
        ticket.name.toLowerCase().includes(searchTerm) ||
        ticket.description.toLowerCase().includes(searchTerm) ||
        ticket.organization.toLowerCase().includes(searchTerm) ||
        ticket.status.toLowerCase().includes(searchTerm) ||
        ticket.assignedTo.toLowerCase().includes(searchTerm)
      );
    }

    // Customer filter - Fixed version
    if (filters.customer) {
      const searchTerm = filters.customer.toLowerCase();
      filtered = filtered.filter(ticket => 
        ticket.customer.toLowerCase().includes(searchTerm)
      );
    }

    // Organization filter
    if (filters.organization) {
      const searchTerm = filters.organization.toLowerCase();
      filtered = filtered.filter(ticket => 
        ticket.organization.toLowerCase().includes(searchTerm)
      );
    }

    // Assignee filter
    if (filters.assignedTo) {
      filtered = filtered.filter(ticket => 
        ticket.assignedTo === filters.assignedTo
      );
    }

    // Date range filter
    if (filters.startDate && filters.endDate) {
      filtered = filtered.filter(ticket => {
        const ticketDate = new Date(ticket.addedOn);
        const startDate = new Date(filters.startDate as unknown as Date);
        startDate.setHours(0, 0, 0, 0);
        const endDate = new Date(filters.endDate as unknown as Date);
        endDate.setHours(23, 59, 59, 999);
        
        return ticketDate >= startDate && ticketDate <= endDate;
      });
    }

    return filtered;
  }, [tickets, filters]);

  return (
    <div className="px-6 pb-3 overflow-hidden overflow-y-auto h-full relative">
      <div className="pt-6 mb-6">
        <TicketsHeader 
          selectedTime={selectedTime} 
          setSelectedTime={setSelectedTime} 
        />
      </div>
      
      {/* Sticky header and filters */}
      <div className="sticky top-0 z-10 bg-[#232323] pb-2 pt-2">
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-xl font-semibold text-white">Tickets Management</h1>
        </div>
        <TicketsFilters 
          filters={filters}
          setFilters={setFilters}
        />
      </div>
      
      {/* Table with scrollable content */}
      <div className="mt-4">
        <TicketsTable tickets={filteredTickets} onAssigneeChange={handleAssigneeChange} />
      </div>

      <TicketModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        mode="create"
      />
    </div>
  );
} 